
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {FirewallActionType} from './FirewallActionType';
import {FieldModelFirewallActionType} from './FirewallActionType';
import {IPv4Range} from './IPv4Range';
import {FieldModelIPv4Range} from './IPv4Range';

/**
 * FirewallRule struct
 */
class FirewallRule {
  /**
   * Initialize struct
   * @param {!UUID=} id
   * @param {!string=} name
   * @param {!string=} description
   * @param {!number=} sortOrder
   * @param {!FirewallActionType=} actionType
   * @param {!IPv4Range=} iPv4Range
   * @constructor
   */
  constructor (id = UUID.sequential(), name = '', description = '', sortOrder = 0, actionType = new FirewallActionType(), iPv4Range = new IPv4Range()) {
    this.id = id
    this.Name = name
    this.Description = description
    this.SortOrder = sortOrder
    this.ActionType = actionType
    this.IPv4Range = iPv4Range
  }

  /**
   * Copy struct (shallow copy)
   * @this {!FirewallRule}
   * @param {!FirewallRule} other Other struct
   * @returns {!FirewallRule} This struct
   */
  copy (other) {
    if (other.id != null) {
      this.id = new UUID(other.id)
    } else {
      this.id = null
    }
    if (other.Name != null) {
      this.Name = other.Name
    } else {
      this.Name = null
    }
    if (other.Description != null) {
      this.Description = other.Description
    } else {
      this.Description = null
    }
    if (other.SortOrder != null) {
      this.SortOrder = other.SortOrder
    } else {
      this.SortOrder = null
    }
    if (other.ActionType != null) {
      this.ActionType = FirewallActionType.fromObject(other.ActionType)
    } else {
      this.ActionType = null
    }
    if (other.IPv4Range != null) {
      this.IPv4Range = IPv4Range.fromObject(other.IPv4Range)
    } else {
      this.IPv4Range = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!FirewallRule}
   * @returns {!FirewallRule} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new FirewallRuleModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new FirewallRuleModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!FirewallRule}
   * @param {!FirewallRule} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof FirewallRule)) {
      throw new TypeError('Instance of FirewallRule is required!')
    }
    // noinspection RedundantIfStatementJS
    if (this.id != null) {
      if ((other.id == null) || !this.id.eq(other.id)) {
        return false
      }
    } else if (other.id != null) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!FirewallRule}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      id: ((this.id != null) ? this.id.toString() : null),
      Name: ((this.Name != null) ? this.Name : null),
      Description: ((this.Description != null) ? this.Description : null),
      SortOrder: ((this.SortOrder != null) ? this.SortOrder : null),
      ActionType: ((this.ActionType != null) ? this.ActionType : null),
      IPv4Range: ((this.IPv4Range != null) ? this.IPv4Range : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return FirewallRule.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!FirewallRule} other Object value
   * @returns {!FirewallRule} Created struct
   */
  static fromObject (other) {
    return new FirewallRule().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!FirewallRule}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return FirewallRule.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!FirewallRule}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 210
  }
}

export { FirewallRule };

/**
 * Fast Binary Encoding FirewallRule field model
 */
class FieldModelFirewallRule extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.id = new fbe.FieldModelUUID(buffer, 4 + 4)
    this.Name = new fbe.FieldModelString(buffer, this.id.fbeOffset + this.id.fbeSize)
    this.Description = new fbe.FieldModelString(buffer, this.Name.fbeOffset + this.Name.fbeSize)
    this.SortOrder = new fbe.FieldModelUInt32(buffer, this.Description.fbeOffset + this.Description.fbeSize)
    this.ActionType = new FieldModelFirewallActionType(buffer, this.SortOrder.fbeOffset + this.SortOrder.fbeSize)
    this.IPv4Range = new FieldModelIPv4Range(buffer, this.ActionType.fbeOffset + this.ActionType.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelFirewallRule}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelFirewallRule}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.id.fbeSize + this.Name.fbeSize + this.Description.fbeSize + this.SortOrder.fbeSize + this.ActionType.fbeSize + this.IPv4Range.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelFirewallRule}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.id.fbeExtra + this.Name.fbeExtra + this.Description.fbeExtra + this.SortOrder.fbeExtra + this.ActionType.fbeExtra + this.IPv4Range.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelFirewallRule}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelFirewallRule.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelFirewallRule}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 210
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelFirewallRule}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelFirewallRule}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.id.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Name.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    if ((fbeCurrentSize + this.Description.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Description.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.SortOrder.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SortOrder.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SortOrder.fbeSize

    if ((fbeCurrentSize + this.ActionType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ActionType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ActionType.fbeSize

    if ((fbeCurrentSize + this.IPv4Range.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IPv4Range.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IPv4Range.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelFirewallRule}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelFirewallRule}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelFirewallRule}
   * @param {!FirewallRule} fbeValue Default value, defaults is new FirewallRule()
   * @returns {!FirewallRule} FirewallRule value
   */
  get (fbeValue = new FirewallRule()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelFirewallRule}
   * @param {!FirewallRule} fbeValue FirewallRule value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) <= fbeStructSize) {
      fbeValue.id = this.id.get(UUID.sequential())
    } else {
      fbeValue.id = UUID.sequential()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) <= fbeStructSize) {
      fbeValue.Name = this.Name.get()
    } else {
      fbeValue.Name = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    if ((fbeCurrentSize + this.Description.fbeSize) <= fbeStructSize) {
      fbeValue.Description = this.Description.get()
    } else {
      fbeValue.Description = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.SortOrder.fbeSize) <= fbeStructSize) {
      fbeValue.SortOrder = this.SortOrder.get()
    } else {
      fbeValue.SortOrder = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SortOrder.fbeSize

    if ((fbeCurrentSize + this.ActionType.fbeSize) <= fbeStructSize) {
      fbeValue.ActionType = this.ActionType.get()
    } else {
      fbeValue.ActionType = new FirewallActionType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ActionType.fbeSize

    if ((fbeCurrentSize + this.IPv4Range.fbeSize) <= fbeStructSize) {
      fbeValue.IPv4Range = this.IPv4Range.get()
    } else {
      fbeValue.IPv4Range = new IPv4Range()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IPv4Range.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelFirewallRule}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelFirewallRule}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelFirewallRule}
   * @param {!FirewallRule} fbeValue FirewallRule value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelFirewallRule}
   * @param {FirewallRule} fbeValue FirewallRule value
   */
  setFields (fbeValue) {
    this.id.set(fbeValue.id)
    this.Name.set(fbeValue.Name)
    this.Description.set(fbeValue.Description)
    this.SortOrder.set(fbeValue.SortOrder)
    this.ActionType.set(fbeValue.ActionType)
    this.IPv4Range.set(fbeValue.IPv4Range)
  }
}

export { FieldModelFirewallRule };

/**
 * Fast Binary Encoding FirewallRule model
 */
class FirewallRuleModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelFirewallRule(this.buffer, 4)
  }

  /**
   * Get the FirewallRule model
   * @this {!FirewallRuleModel}
   * @returns {!FieldModelFirewallRule} model FirewallRule model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!FirewallRuleModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!FirewallRuleModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return FirewallRuleModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!FirewallRuleModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelFirewallRule.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!FirewallRuleModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!FirewallRuleModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!FirewallRuleModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!FirewallRuleModel}
   * @param {!FirewallRule} value FirewallRule value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!FirewallRuleModel}
   * @param {!FirewallRule} value FirewallRule value, defaults is new FirewallRule()
   * @return {!object} Deserialized FirewallRule value and its size
   */
  deserialize (value = new FirewallRule()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new FirewallRule(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new FirewallRule(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!FirewallRuleModel}
   * @param {!number} prev Previous FirewallRule model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { FirewallRuleModel };
